import {
  Button,
  Checkbox,
  Collapse,
  type CollapseProps,
  DatePicker,
  Flex,
  Form,
  InputNumber,
  Select,
  theme,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import { type ReactNode, useEffect } from 'react';
import { catchError, EMPTY, from, of, switchMap, tap, throwError } from 'rxjs';
import { Customer } from '../../../entities';
import SpecialEventMasterSettings from '../../../entities/special-event-master-settings';
import { useNotification } from '../../../hooks/use-notification';
import { useQuery } from '../../../jsonapi-provider';
import { LocalDate } from '../../../utils/local-date';
import type dayjs from 'dayjs';

const MasterSettingsPage = (): ReactNode => {
  const notification = useNotification();
  const { token } = theme.useToken();
  const [formInstance] = useForm();

  const customers = useQuery<Customer[]>([
    'customer',
    {
      filter: 'isInactive==false',
      page: {
        size: 10000,
      },
    },
  ]);
  const specialEventMasterSettings = useQuery<SpecialEventMasterSettings[]>([
    'special-event-master-settings',
    {
      include: ['availableCustomers'],
    },
  ]);
  const onSubmit = (): void => {
    from(formInstance.validateFields())
      .pipe(
        switchMap((values) => {
          const specialEventMesterSettingsToBeUpdated = new SpecialEventMasterSettings(
            specialEventMasterSettings.data?.[0].id,
            values.isEnabled,
            values.minNumberOfMeals,
            values.deadlineToOrder,
            values.dateRange[0],
            values.dateRange[1],
            values.availableCustomers.includes('all')
              ? customers.data?.map((cust) => {
                  const customer = new Customer();
                  customer.id = cust.id;
                  return customer;
                })
              : values.availableCustomers.map((cust: string) => {
                  const customer = new Customer();
                  customer.id = cust;
                  return customer;
                })
          );
          return from(
            specialEventMasterSettings.client.mutate(
              ['special-event-master-settings', specialEventMasterSettings.data?.[0].id],
              specialEventMesterSettingsToBeUpdated
            )
          ).pipe(
            switchMap((res) =>
              res.error ? throwError(() => new Error(res.error?.detail)) : of(res.data)
            ),
            tap(() => {
              notification.success({
                description: 'Settings updated successfully',
                message: '',
              });
            }),
            catchError((error: Error) => {
              notification.error({
                description: error.message,
                message: 'Failed to update settings',
              });
              return EMPTY;
            })
          );
        })
      )
      .subscribe();
  };
  useEffect(() => {
    formInstance.setFieldsValue({
      ...(specialEventMasterSettings.data?.[0] ?? new SpecialEventMasterSettings()),

      dateRange: [
        specialEventMasterSettings.data?.[0].startDate,
        specialEventMasterSettings.data?.[0].endDate,
      ],
    });
    formInstance.setFieldValue(
      'availableCustomers',
      specialEventMasterSettings.data?.[0].availableCustomers.length === customers.data?.length
        ? ['all']
        : specialEventMasterSettings.data?.[0].availableCustomers.map((customer) => customer.id)
    );
  }, [specialEventMasterSettings.data, customers.data]);
  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: 'Special Events',
      children: (
        <Flex gap="large" flex={2} style={{ marginInlineStart: token.marginXL }} vertical>
          <Form.Item
            colon={false}
            name="isEnabled"
            valuePropName="checked"
            style={{ marginBlockEnd: 0 }}
          >
            <Checkbox>Enable special events</Checkbox>
          </Form.Item>
          <Form.Item
            colon={false}
            name="minNumberOfMeals"
            style={{ marginBlockEnd: 0 }}
            label="Minimum number of meals"
          >
            <InputNumber addonAfter="meals" min={0} precision={0} />
          </Form.Item>
          <Form.Item
            colon={false}
            name="deadlineToOrder"
            style={{ marginBlockEnd: 0 }}
            label="Deadline to order"
          >
            <InputNumber addonAfter="days" min={0} precision={0} />
          </Form.Item>
          <Form.Item
            label="Date range"
            name="dateRange"
            getValueProps={(value: [LocalDate, LocalDate]) => ({
              value: value?.map((d) => d?.toDayjs()),
            })}
            normalize={(value) => value?.map((d: dayjs.Dayjs) => new LocalDate(d))}
          >
            <DatePicker.RangePicker />
          </Form.Item>
          <Form.Item name="availableCustomers" label="Available for customers">
            <Select
              mode="multiple"
              allowClear
              style={{ minWidth: 300, maxWidth: 900 }}
              placeholder="Please select customers"
              options={[
                { label: 'All', value: 'all' },
                ...(customers.data ?? []).map((customer: Customer) => ({
                  label: customer.name,
                  value: customer.id,
                })),
              ]}
              onChange={(values) => {
                if (values?.length && values[values.length - 1] === 'all') {
                  formInstance.setFieldValue('availableCustomers', 'all');
                } else {
                  if (values?.length && values[0] === 'all') {
                    const filteredSelected = values.filter((v: string) => v !== 'all');
                    formInstance.setFieldValue('availableCustomers', filteredSelected);
                  } else {
                    formInstance.setFieldValue('availableCustomers', values);
                  }
                }
              }}
            />
          </Form.Item>
        </Flex>
      ),
    },
  ];
  return (
    <Form id="master-setting-form" onFinish={onSubmit} layout="vertical" form={formInstance}>
      <Collapse size="large" items={items} bordered={false} defaultActiveKey={['1']}></Collapse>

      <Flex gap="small" style={{ flexDirection: 'row-reverse', marginTop: 20, marginBottom: 20 }}>
        <Button htmlType="submit" type="primary">
          save
        </Button>
      </Flex>
    </Form>
  );
};

MasterSettingsPage.displayName = 'MasterSettingsPage';
export default MasterSettingsPage;
